import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useMediaQuery } from '@mui/material';
import { BotData } from '@wt/app/admin/_components/providers/botForm/types';
import { useBannerAdOpen } from '@wt/game/providers/bannerAdOpen/BannerAdOpenProvider';
import useSettingsContext from '@wt/game/providers/settings/types';
import { HtmlTooltip } from '@wt/game/providers/tooltips';
import { PuzzleLocation } from '@wt/shared/components/maps/mapkit/types';
import SanitizedHTML from '@wt/shared/components/SanitizedHTML';
import TypewriterComponent from '@wt/shared/components/typewriter/Typewriter';
import { getBotData } from '@wt/utilities/database';
import { getSiteName } from '@wt/utilities/siteMode';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';

import { MobileRoundScores } from './RoundResultsMobile';

const Accordion = dynamic(() => import('@mui/material/Accordion'), {
  ssr: false,
});
const AccordionDetails = dynamic(
  () => import('@mui/material/AccordionDetails'),
  { ssr: false }
);
const AccordionSummary = dynamic(
  () => import('@mui/material/AccordionSummary'),
  { ssr: false }
);

const BotInfo = ({ round }: { round: PuzzleLocation }) => {
  const [botData, setBotData] = useState<BotData | null>();
  const botRef = useRef<HTMLDivElement>(null);
  const [autoOpen, setAutoOpen] = useState<
    false | 'image-analysis' | 'historical-context'
  >(false);
  const [animationComplete, setAnimationComplete] = useState<boolean>(false);
  const { adOpen } = useBannerAdOpen();
  const [settings] = useSettingsContext();

  const WTBotTurnedOff = useMemo(() => {
    return (
      settings?.WTBot?.botGuess === false &&
      settings?.WTBot?.historicalContext === false &&
      settings?.WTBot?.imageAnalysis === false
    );
  }, [settings?.WTBot]);

  useEffect(() => {
    getBotData(round.puzzle)
      .then((d) => {
        if (!d) return;

        setBotData(d);

        // scroll to bot info after 5 seconds if turned on
        if (!WTBotTurnedOff) {
          setTimeout(() => {
            if (botRef.current) {
              botRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }

            // auto open after scroll
            if (
              settings?.WTBot?.historicalContext !== false ||
              settings?.WTBot?.imageAnalysis !== false
            ) {
              setTimeout(() => {
                setAutoOpen(
                  settings?.WTBot?.historicalContext !== false
                    ? 'historical-context'
                    : 'image-analysis'
                );
              }, 800);
            }
          }, 7500);
        }
      })
      .catch((e) => console.log(e));
  }, [round.puzzle]);

  if (!botData) return <></>;

  return (
    <div
      ref={botRef}
      className={
        'mb-5 flex w-full flex-col gap-4 ipad:scroll-mt-0 desktop:scroll-mt-0 ' +
        (adOpen ? 'scroll-mt-[120px]' : 'scroll-mt-[85px]')
      }
    >
      {settings?.WTBot?.botGuess !== false && (
        <BotGuessBox round={round} botData={botData} />
      )}
      {settings?.WTBot?.imageAnalysis !== false && (
        <BotImageAnalysis
          botData={botData}
          autoOpenAccordian={autoOpen === 'image-analysis'}
          onFinished={() => {
            if (autoOpen === 'image-analysis') {
              setAnimationComplete(true);
            }
          }}
        />
      )}
      {settings?.WTBot?.historicalContext !== false && (
        <BotHistoricalContext
          botData={botData}
          autoOpenAccordian={autoOpen === 'historical-context'}
          onFinished={() => setAnimationComplete(true)}
        />
      )}
      <BotLearnMore botData={botData} />

      {/** allows for bot info to scroll to top of parent container */}
      <div
        style={{
          height: animationComplete || WTBotTurnedOff ? '0px' : '500px',
        }}
      ></div>
    </div>
  );
};

export default BotInfo;

const BotGuessBox = ({
  round,
  botData,
}: {
  round: PuzzleLocation;
  botData: BotData;
}) => {
  return (
    <div className="relative">
      <BotInfoAccordian
        title={
          <>
            <InfoButton />
            <div className="pl-5 text-black dark:text-base-300">
              🤖 {getSiteName()} Bot Guess 🤖
            </div>
          </>
        }
        titleStyles="text-lg font-bold text-highlight lg:text-xl"
        details={
          <div className="ml-3 mr-9 flex flex-col justify-center gap-3">
            <div className="flex w-full justify-center gap-4 text-base lg:text-lg">
              <div>
                📍 {botData.city}, {botData.country}
              </div>
              <div>🗓️ {botData.year}</div>
            </div>
            <MobileRoundScores
              guess={{
                puzzle: round,
                year: botData.year,
                location: {
                  latitude: botData.latitude,
                  longitude: botData.longitude,
                },
              }}
              unit={'km'}
              botGuess={true}
            />
          </div>
        }
      />
    </div>
  );
};

const InfoButton = () => {
  const [, , , setSettingsModalOpen] = useSettingsContext();
  const isMobile = useMediaQuery('(pointer: coarse)');
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    if (isMobile) {
      setOpen((prev) => !prev);
    }
  };

  return (
    <HtmlTooltip
      arrow
      open={isMobile ? open : undefined}
      onClose={isMobile ? () => setOpen(false) : undefined}
      disableFocusListener={isMobile}
      disableHoverListener={isMobile}
      disableTouchListener={isMobile}
      title={
        <div className="flex w-full flex-col gap-3 text-sm">
          <p>
            Hi, I'm the {getSiteName()} Bot 🤖. My human overlords created me
            to:
          </p>
          <ol>
            <li>1. Play the game (guess location + year)</li>
            <li>2. Provide an image analysis</li>
            <li>3. Draft a short history lesson</li>
          </ol>
          <p>
            I hope I can make the game more fun and educational for you! If you
            have questions, check out the{' '}
            <Link href="/#faq" className="underline">
              FAQ
            </Link>
            .
          </p>
          <p>
            You can choose what content I display or turn me off in{' '}
            <Link
              href="#"
              className="underline"
              onClick={() => setSettingsModalOpen(true)}
            >
              Settings
            </Link>
            .
          </p>
        </div>
      }
    >
      <div
        onClick={handleClick}
        className="absolute left-0 top-[-0.75rem] translate-y-[50%] flex size-6 cursor-pointer items-center justify-center rounded-full bg-highlight text-md text-center font-bold text-white hover:scale-125"
      >
        i
      </div>
    </HtmlTooltip>
  );
};

const BotImageAnalysis = ({
  botData,
  autoOpenAccordian,
  onFinished,
}: {
  botData: BotData;
  autoOpenAccordian: boolean;
  onFinished: () => void;
}) => {
  const [runLocation, setRunLocation] = useState<boolean>(false);
  const [showYear, setShowYear] = useState<boolean>(false);
  const [runYear, setRunYear] = useState<boolean>(false);
  const [skip, setSkip] = useState(false);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [hidingToast, setHidingToast] = useState(false);

  const shouldShowButton = !skip && showSkipButton;

  useEffect(() => {
    if (showToast) {
      const t = setTimeout(() => setHidingToast(true), 1700);
      return () => clearTimeout(t);
    }
  }, [showToast]);

  return (
    <BotInfoAccordian
      title={'🧐 Image Analysis 🤖'}
      autoOpenAccordian={autoOpenAccordian}
      onOpenCallback={() => {
        if (!runLocation) {
          setRunLocation(true);
          setTimeout(() => {
            setShowSkipButton(true);
          }, 500);
        }
      }}
      details={
        <div className="mx-3 flex flex-col gap-5 text-sm dark:text-info-content relative">
          {shouldShowButton && (
            <div
              className="absolute top-[-30px] right-[-12px]
                  animate-[fade-and-scale-in_0.3s_ease-out_0.3s_both]"
            >
              <button
                className="flex items-center justify-center px-2 py-1 rounded
                text-2xl bg-primary hover:bg-[#5B08D5] text-[#fef6fe]
                 transition-transform duration-150 active:scale-90"
                onClick={() => {
                  setSkip(true);
                  setShowToast(true);
                }}
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                  <path d="M2 3L7 8L2 13V3z" />
                  <path d="M7 3L12 8L7 13V3z" />
                  <rect x="13" y="3" width="1.5" height="10" />
                </svg>
              </button>
            </div>
          )}
          {showToast && (
            <div
              className={`absolute top-[-30px] right-[-12px] 
                bg-[#FFF8DD] border border-[#E5D3A3] text-black
                px-4 py-1 rounded-lg pointer-events-none
                ${
                  hidingToast
                    ? 'animate-[fade-and-scale-out_0.3s_ease-in_forwards]'
                    : 'animate-[fade-and-scale-in_0.3s_ease-out_0.3s_both]'
                }`}
              onAnimationEnd={() => {
                if (hidingToast) {
                  setShowToast(false);
                  setHidingToast(false);
                }
              }}
            >
              ✓ Done
            </div>
          )}
          <div className="flex flex-col gap-3">
            <div className="text-base font-bold">📍 Location</div>
            <TypewriterComponent
              htmlString={botData.locationAnalysis}
              runAnimation={!skip && runLocation}
              showFull={skip}
              onFinished={() => {
                setShowYear(true);
                setTimeout(() => {
                  setRunYear(true);
                }, 500);
              }}
            />
          </div>
          {(showYear || skip) && (
            <div className="flex flex-col gap-3">
              <div className="text-base font-bold">🗓️ Year</div>
              <TypewriterComponent
                htmlString={botData.yearAnalysis}
                runAnimation={!skip && runYear}
                showFull={skip}
                onFinished={() => {
                  onFinished();
                  setTimeout(() => {
                    setShowSkipButton(false);
                  }, 500);
                }}
              />
            </div>
          )}
        </div>
      }
    />
  );
};

const BotHistoricalContext = ({
  botData,
  autoOpenAccordian,
  onFinished,
}: {
  botData: BotData;
  autoOpenAccordian: boolean;
  onFinished: () => void;
}) => {
  const [run, setRun] = useState<boolean>(false);
  const [skip, setSkip] = useState(false);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [hidingToast, setHidingToast] = useState(false);

  const shouldShowButton = !skip && showSkipButton;

  useEffect(() => {
    if (showToast) {
      const t = setTimeout(() => setHidingToast(true), 1700);
      return () => clearTimeout(t);
    }
  }, [showToast]);

  return (
    <BotInfoAccordian
      title={'📺 Historical Context 🤖'}
      autoOpenAccordian={autoOpenAccordian}
      onOpenCallback={() => {
        if (!run) {
          setRun(true);
          setTimeout(() => {
            setShowSkipButton(true);
          }, 500);
        }
      }}
      details={
        <div className="mx-3 flex flex-col text-sm dark:text-info-content relative">
          {shouldShowButton && (
            <div
              className="absolute top-[-30px] right-[-12px]
                  animate-[fade-and-scale-in_0.3s_ease-out_0.3s_both]"
            >
              <button
                className="flex items-center justify-center px-2 py-1 rounded
                text-2xl bg-primary hover:bg-[#5B08D5] text-[#fef6fe]
                 transition-transform duration-150 active:scale-90"
                onClick={() => {
                  setSkip(true);
                  setShowToast(true);
                }}
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                  <path d="M2 3L7 8L2 13V3z" />
                  <path d="M7 3L12 8L7 13V3z" />
                  <rect x="13" y="3" width="1.5" height="10" />
                </svg>
              </button>
            </div>
          )}
          {showToast && (
            <div
              className={`absolute top-[-30px] right-[-12px] 
                bg-[#FFF8DD] border border-[#E5D3A3] text-black
                px-4 py-1 rounded-lg pointer-events-none
                ${
                  hidingToast
                    ? 'animate-[fade-and-scale-out_0.3s_ease-in_forwards]'
                    : 'animate-[fade-and-scale-in_0.3s_ease-out_0.3s_both]'
                }`}
              onAnimationEnd={() => {
                if (hidingToast) {
                  setShowToast(false);
                  setHidingToast(false);
                }
              }}
            >
              ✓ Done
            </div>
          )}
          <TypewriterComponent
            htmlString={botData.historicalContext.concat(`<br /><p><em>
            Please note that like other AI's, I can sometimes make mistakes or
            hallucinate facts, so please approach my content with a critical eye
            and do your own fact-checking.
          </p><em> <br />`)}
            runAnimation={run}
            showFull={skip}
            onFinished={() => {
              onFinished();
              setTimeout(() => {
                setShowSkipButton(false);
              }, 500);
            }}
          />
        </div>
      }
    />
  );
};

const BotLearnMore = ({ botData }: { botData: BotData }) => {
  return (
    <BotInfoAccordian
      title={'🔍 Learn More'}
      details={
        <div className="mx-3 flex flex-col gap-5 text-sm dark:text-info-content">
          <SanitizedHTML htmlString={botData.learnMore} />
        </div>
      }
    />
  );
};

const BotInfoAccordian = ({
  title,
  titleStyles,
  details,
  onOpenCallback,
  autoOpenAccordian,
}: {
  title: JSX.Element | string;
  titleStyles?: string;
  details: JSX.Element;
  onOpenCallback?: () => void;
  autoOpenAccordian?: boolean;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (autoOpenAccordian && !open) {
      setOpen(true);
      setTimeout(() => {
        onOpenCallback && onOpenCallback();
      }, 500);
    }
  }, [autoOpenAccordian]);

  return (
    <div className="flex w-full flex-col rounded-2xl bg-base-200 py-1">
      <Accordion
        className="m-0 flex w-full flex-col items-center"
        sx={{
          boxShadow: 'none',
          backgroundColor: 'transparent',
          '&:before': {
            display: 'none',
          },
          backgroundImage: 'none',
          '&.MuiPaper-root': {
            margin: 0,
          },
          '& .MuiAccordionDetails-root': {
            margin: 0,
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
          '& .MuiCollapse-root': {
            minWidth: '100%',
          },
        }}
        expanded={open}
        onChange={(e, expanded) => {
          setOpen(expanded);
          if (expanded) {
            onOpenCallback && onOpenCallback();
          }
        }}
      >
        <AccordionSummary
          className="flex w-full hover:cursor-pointer"
          expandIcon={<ExpandMoreRoundedIcon className="text-base-300" />}
        >
          <div
            className={`relative flex w-full justify-center text-center ${titleStyles ?? 'text-lg text-black font-bold dark:text-base-300'}`}
          >
            {title}
          </div>
        </AccordionSummary>
        <AccordionDetails>{details}</AccordionDetails>
      </Accordion>
    </div>
  );
};
